/**
 * Groups a chat thread by the conversation its messages were promoted onto.
 *
 * Social threads run continuously while the conversations raised from them are
 * discrete, so the thread reads best as runs of loose messages separated by
 * collapsible conversation blocks — the same shape the KC2 interaction view uses.
 * Shared by the WhatsApp and Facebook threads so the two can't drift.
 */

export interface GroupableMessage {
  id: string
  conversation_id: string | null
  sent_at: string
}

export interface ConversationRef {
  id: string
  reference_id: string
  status: string
  subject: string | null
  is_archived: boolean
}

export type MessageGroup<T> =
  | { type: 'loose'; key: string; messages: T[] }
  | { type: 'ticket'; key: string; conversationId: string; messages: T[] }

/**
 * Messages stay in thread order. A run of unlinked messages becomes one `loose`
 * group; messages sharing a conversation_id collapse into that conversation's block
 * even when other messages were interleaved between them in time.
 */
export function groupMessagesByTicket<T extends GroupableMessage>(messages: T[]): Array<MessageGroup<T>> {
  const groups: Array<MessageGroup<T>> = []
  let loose: T[] = []

  const flushLoose = (): void => {
    if (loose.length > 0) {
      groups.push({ type: 'loose', key: `loose-${loose[0]!.id}`, messages: loose })
      loose = []
    }
  }

  for (const msg of messages) {
    if (!msg.conversation_id) {
      loose.push(msg)
      continue
    }
    flushLoose()
    const existing = groups.find(
      (g): g is Extract<MessageGroup<T>, { type: 'ticket' }> =>
        g.type === 'ticket' && g.conversationId === msg.conversation_id,
    )
    if (existing) existing.messages.push(msg)
    else
      groups.push({
        type: 'ticket',
        key: `ticket-${msg.conversation_id}`,
        conversationId: msg.conversation_id,
        messages: [msg],
      })
  }
  flushLoose()
  return groups
}

/** Tailwind classes per conversation status, so the chip reads at a glance. */
export function statusChipClass(status: string): string {
  switch (status.toLowerCase()) {
    case 'open':
    case 'new':
      return 'bg-emerald-100 text-emerald-700'
    case 'pending':
    case 'waiting':
    case 'on_hold':
      return 'bg-amber-100 text-amber-700'
    case 'resolved':
    case 'closed':
      return 'bg-gray-200 text-gray-600'
    default:
      return 'bg-blue-100 text-blue-700'
  }
}

export function statusLabel(status: string): string {
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Closed conversations start collapsed — they're history, not the live conversation. */
export function isResolvedStatus(status: string): boolean {
  return ['closed', 'resolved'].includes(status.toLowerCase())
}
